import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Grid } from '@mui/material'

import { useUpdateUserSettingMutation } from '../../api/combined'
import AccountBadge from '../../features/account/components/AccountBadge/AccountBadge'
import { UserSettingKeys } from '../../features/account/types/UserSettings'
import { GameSearchInput } from '../../features/game'
import { useGames } from '../../features/game/hooks/gameHooks'
import { MarketSelector, useCurrentMarket } from '../../features/markets'
import { isEmpty } from '../../features/news/utils/utils'
import { useMobileSize } from '../../features/responsiveness/hooks/responsivenessHooks'
import { useAppSelector } from '../../hooks/storeHooks'
import { useInternalRouteMatch } from '../../hooks/useInternalRouteMatch'
import { Page } from '../../types/Page'
import BetaBadge from '../BetaBadge/BetaBadge'
import './AppHeader.scss'

const pagesUseGameName = ['game', 'game-analyzer-detail']

interface AppHeaderProps {}

const AppHeader: React.FC<AppHeaderProps> = () => {
  const isInternal = useInternalRouteMatch()
  const currentPage: Page | undefined = useAppSelector((state) => {
    return state.currentPage
  })
  const mobileSize = useMobileSize()
  const [saveUserSetting] = useUpdateUserSettingMutation()

  const displayMarketSelector = !isInternal
  const displayGameSearch = !isInternal && !mobileSize
  const displayUserBadge = !mobileSize

  const onMarketSelectorChange = (marketIso: string) => {
    saveUserSetting({ settingKey: UserSettingKeys.userMarket, value: { iso: marketIso } })
  }

  return (
    <div className={!mobileSize ? 'AppHeader AppHeader--sticky' : 'AppHeader'}>
      <div id="app-header">
        <div id="app-page-name">
          <HeaderName />
          {currentPage?.showBetaBadge && <BetaBadge sx={{ margin: 0, marginLeft: '10px', transform: 'translateY(1px)' }} variant="outlined" />}
        </div>
        <Grid item container flexBasis="50%" alignItems="center" justifyContent="flex-end" spacing="10px">
          {displayMarketSelector && (
            <Grid item id="app-market-switcher">
              <MarketSelector onChange={onMarketSelectorChange} />
            </Grid>
          )}

          {displayGameSearch && (
            <Grid item id="app-game-search">
              <GameSearchInput />
            </Grid>
          )}

          {displayUserBadge && (
            <Grid item id="app-user-badge" ml={-1}>
              <AccountBadge />
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  )
}

export default AppHeader

const HeaderName: FC<{}> = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const currentPage: Page | undefined = useAppSelector((state) => {
    return state.currentPage
  })
  const [gameId, setGameId] = useState<string | undefined>()
  const { currentMarketIso: marketIso } = useCurrentMarket()
  const {
    games: { 0: game },
  } = useGames(gameId ? [gameId] : [], marketIso)

  useEffect(() => {
    if (!!currentPage) {
      const pathName = location.pathname.split('/')
      if (pagesUseGameName.includes(currentPage.id) && !!pathName[2]) {
        setGameId(pathName[2])
      } else {
        setGameId(undefined)
      }
    }
  }, [currentPage, location.pathname])

  const headerName = useMemo((): string | null => {
    if (!currentPage) return null
    if (gameId && game) {
      return !isEmpty(game.resolvedName)
        ? game.resolvedName
        : t('common:game_by', {
            createdBy: game.createdBy,
          })
    }
    return currentPage.title ? t(currentPage.title) : t(currentPage.localizationKey)
  }, [currentPage, game, gameId, t])

  return <>{headerName}</>
}
